import React from 'react';
import { libelleRole } from '../../constants/contactEnums';
import { getMainPhone, getMainEmail, numeroWhatsapp } from '../../utils/contactProfile';
import SelecteurTelephone from '../SelecteurTelephone';

/**
 * lot DD — liste des contacts, rendu TÉLÉPHONE (< 768 px).
 * Ne contient AUCUNE logique de filtrage ni de tri : `ContactList.jsx` reste seul maître et
 * fournit la liste déjà filtrée et triée, les compteurs et les fonctions de filtre.
 */
const PASTILLES = [
  { id: 'tous', label: 'Tous' },
  { id: 'acquéreurs', label: 'Acquéreurs' },
  { id: 'propriétaires', label: 'Mandants' },
];

const initiales = (c) => `${(c.prenom || '').charAt(0)}${(c.nom || '').charAt(0)}`.toUpperCase()
  || (c.nom_societe || '?').charAt(0).toUpperCase();

/** Numéro WhatsApp : règle commune à la liste et à la fiche (`utils/contactProfile.js`). */
export { numeroWhatsapp };

export const ContactListMobile = ({
  contacts,
  total,
  searchQuery,
  onSearch,
  filterType,
  onFilterType,
  showArchived,
  onShowArchived,
  countByType,
  archivedCount,
  onOpenContact,
  onNew,
  onOpenFilters,
  filtresActifs = 0,
  affichables,
  onAfficherPlus,
}) => (
  <div className="md:hidden bg-slate-50 min-h-full" data-testid="contact-list-mobile">
    <div className="sticky top-0 z-20 bg-slate-50 px-4 pt-3 pb-2 border-b border-slate-200">
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-base font-bold text-slate-900">
          Contacts <span className="text-slate-400 font-semibold num">{total}</span>
        </h1>
        <button
          type="button"
          onClick={onNew}
          aria-label="Nouveau contact"
          data-testid="contact-mobile-new-btn"
          className="w-11 h-11 rounded-xl bg-blue-600 text-white flex items-center justify-center"
        >
          <i className="bi bi-person-plus text-lg" />
        </button>
      </div>

      <div className="relative mt-2">
        <i className="bi bi-search absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          type="search"
          value={searchQuery}
          onChange={(e) => onSearch(e.target.value)}
          placeholder="Rechercher un contact…"
          data-testid="contact-mobile-search"
          className="w-full h-11 pl-9 pr-3 text-base bg-white border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-200"
        />
      </div>

      {/* lot DK2 — un menu déroulant à la place des pastilles qui défilent */}
      <div className="mt-2 flex items-center gap-2">
        <SelecteurTelephone
          value={showArchived ? 'archives' : filterType}
          onChange={(e) => {
            const choix = e.target.value;
            if (choix === 'archives') { onShowArchived(true); return; }
            onShowArchived(false);
            onFilterType(choix);
          }}
          options={[
            ...PASTILLES.map((p) => ({ valeur: p.id, libelle: `${p.label} (${countByType(p.id)})` })),
            { valeur: 'archives', libelle: `Archivés (${archivedCount})` },
          ]}
          ariaLabel="Filtrer les contacts"
          testId="contact-mobile-tab-select"
          className="flex-1"
        />
        <button
          type="button"
          onClick={onOpenFilters}
          aria-label="Filtres"
          data-testid="contact-mobile-tab-filtres"
          className="relative w-11 h-11 shrink-0 rounded-[10px] bg-white border border-[#CBD5E1] text-slate-600 flex items-center justify-center"
        >
          <i className="bi bi-sliders" />
          {filtresActifs > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-blue-600 text-white text-[10px] font-bold flex items-center justify-center num">
              {filtresActifs}
            </span>
          )}
        </button>
      </div>
    </div>

    {contacts.length === 0 ? (
      <p className="text-sm text-slate-400 text-center py-12" data-testid="contact-mobile-empty">
        Aucun contact
      </p>
    ) : (
      <ul className="divide-y divide-slate-100 bg-white">
        {contacts.map((c) => {
          const tel = getMainPhone(c);
          const mail = getMainEmail(c);
          const nom = [c.prenom, c.nom].filter(Boolean).join(' ').trim() || c.nom_societe || '—';
          return (
            <li key={c.id} className="flex items-center gap-3 px-4 py-3 min-h-[70px]">
              <button
                type="button"
                onClick={() => onOpenContact(c)}
                data-testid={`contact-mobile-row-${c.id}`}
                className="flex-1 min-w-0 flex items-center gap-3 text-left"
              >
                <span className="w-10 h-10 shrink-0 rounded-full bg-slate-100 text-slate-600 text-sm font-bold flex items-center justify-center">
                  {initiales(c)}
                </span>
                <span className="min-w-0">
                  <span className="block font-semibold text-slate-900 text-sm truncate">{nom}</span>
                  <span className="block text-xs text-slate-500 truncate">
                    {[libelleRole(c.type_contact), c.ville].filter(Boolean).join(' · ') || '—'}
                  </span>
                </span>
              </button>
              {tel ? (
                <span className="flex items-center gap-1 shrink-0">
                  <a
                    href={`tel:${tel}`}
                    aria-label={`Appeler ${nom}`}
                    data-testid={`contact-mobile-call-${c.id}`}
                    className="w-11 h-11 rounded-xl bg-slate-100 text-slate-700 flex items-center justify-center"
                  >
                    <i className="bi bi-telephone" />
                  </a>
                  <a
                    href={`https://wa.me/${numeroWhatsapp(tel)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`WhatsApp ${nom}`}
                    data-testid={`contact-mobile-whatsapp-${c.id}`}
                    className="w-11 h-11 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center"
                  >
                    <i className="bi bi-whatsapp" />
                  </a>
                </span>
              ) : mail ? (
                <a
                  href={`mailto:${mail}`}
                  aria-label={`Écrire à ${nom}`}
                  data-testid={`contact-mobile-mail-${c.id}`}
                  className="w-11 h-11 shrink-0 rounded-xl bg-slate-100 text-slate-700 flex items-center justify-center"
                >
                  <i className="bi bi-envelope" />
                </a>
              ) : null}
            </li>
          );
        })}
      </ul>
    )}

    {affichables < total && (
      <div className="p-4">
        <button
          type="button"
          onClick={onAfficherPlus}
          data-testid="contact-mobile-more-btn"
          className="w-full h-12 rounded-xl bg-white border border-slate-200 text-slate-700 font-semibold"
        >
          Afficher plus
        </button>
      </div>
    )}

    <div style={{ height: 'calc(70px + env(safe-area-inset-bottom, 0px))' }} aria-hidden="true" />
  </div>
);

export default ContactListMobile;
